import type { StepProps } from '../types';
import { pensionTotal } from '@/lib/pensionTotal';
import { CurrencyField } from '../fields';

export default function IncomeStep({ input, onChange, filingStatus }: StepProps) {
  const isMFJ = filingStatus === 'MFJ';
  const computedPensionSum = pensionTotal({ ...input, filingStatus: filingStatus ?? input.filingStatus });

  return (
    <div className="space-y-8">
      <div data-tour="income-fields">
        <h3 className="text-sm font-semibold mb-4">Employment & Earned Income</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CurrencyField label={isMFJ ? "Wages (Taxpayer)" : "Wages, Salaries (W-2)"} value={input.wages} onChange={v => onChange('wages', v)} hint="1040 line 1a" />
          {isMFJ && (
            <CurrencyField label="Wages (Spouse)" value={input.spouseWages} onChange={v => onChange('spouseWages', v)} hint="1040 line 1a (spouse's W-2)" />
          )}
        </div>
      </div>

      <div data-tour="income-pensions">
        <h3 className="text-sm font-semibold mb-1">Pensions & Military Retirement</h3>
        <p className="text-xs text-muted-foreground mb-4">Enter each pension type separately — the total is auto-calculated</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CurrencyField label="Govt Pension (Taxpayer)" value={input.govtPensionTP} onChange={v => onChange('govtPensionTP', v)} hint="1099-R → 1040 line 5b" />
          {isMFJ && (
            <CurrencyField label="Govt Pension (Spouse)" value={input.govtPensionSP} onChange={v => onChange('govtPensionSP', v)} hint="1099-R → 1040 line 5b" />
          )}
          <CurrencyField label="Private Pension (Taxpayer)" value={input.nonGovtPensionTP} onChange={v => onChange('nonGovtPensionTP', v)} hint="1099-R → 1040 line 5b" />
          {isMFJ && (
            <CurrencyField label="Private Pension (Spouse)" value={input.nonGovtPensionSP} onChange={v => onChange('nonGovtPensionSP', v)} hint="1099-R → 1040 line 5b" />
          )}
          <CurrencyField label="Military Retirement" value={input.militaryRetirementPension} onChange={v => onChange('militaryRetirementPension', v)} hint="1099-R → 1040 line 5b" />
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground">Total Pension (auto)</span>
            <div className="h-10 flex items-center rounded-md border bg-muted/50 px-3 text-sm font-mono text-muted-foreground">
              ${computedPensionSum.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      <div data-tour="income-investment">
        <h3 className="text-sm font-semibold mb-4">Investment & Interest Income</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CurrencyField label="Tax-Exempt Interest" value={input.taxExemptInterest} onChange={v => onChange('taxExemptInterest', v)} hint="1040 line 2a" />
          <CurrencyField label="Interest Income (1099-INT)" value={input.interestIncome} onChange={v => onChange('interestIncome', v)} hint="1040 line 2b" />
          <CurrencyField label="Total Dividends" value={input.ordinaryDividends} onChange={v => onChange('ordinaryDividends', v)} hint="1040 line 3b · 1099-DIV box 1a — total dividends, includes qualified" />
          <CurrencyField label="Qualified Dividends" value={input.qualifiedDividends} onChange={v => onChange('qualifiedDividends', v)} hint="1040 line 3a · 1099-DIV box 1b — subset of total, taxed at cap-gains rates" />
        </div>
        {input.qualifiedDividends > input.ordinaryDividends && (
          <p className="mt-3 text-xs text-destructive">
            Qualified Dividends (${input.qualifiedDividends.toLocaleString()}) exceeds Total Dividends (${input.ordinaryDividends.toLocaleString()}). Qualified is a subset of the total — increase Total Dividends to match.
          </p>
        )}
      </div>

      <div data-tour="income-retirement">
        <h3 className="text-sm font-semibold mb-1">Retirement & Social Security</h3>
        <p className="text-xs text-muted-foreground mb-4">IRA distributions, Roth conversions, and Social Security</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CurrencyField label="IRA Taxable Distributions" value={input.iraDistributionConversion} onChange={v => onChange('iraDistributionConversion', v)} hint="1040 line 4b" />
          <CurrencyField label="Roth Conversion Amount" value={input.rothConversionAmount} onChange={v => onChange('rothConversionAmount', v)} hint="Included in 1040 line 4a/4b — 1099-R" />
          <CurrencyField label="Gross Social Security" value={input.grossSocialSecurity} onChange={v => onChange('grossSocialSecurity', v)} hint="1040 line 6a · SSA-1099 box 5" />
          <CurrencyField label="Non-Qualified Annuity" value={input.nonQualifiedAnnuityIncome} onChange={v => onChange('nonQualifiedAnnuityIncome', v)} hint="1099-R → 1040 line 5b" />
        </div>
      </div>

      <div data-tour="income-capgains">
        <h3 className="text-sm font-semibold mb-4">Capital Gains & Other</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CurrencyField label="Short-Term Capital Gains" allowNegative value={input.shortTermCapitalGains} onChange={v => onChange('shortTermCapitalGains', v)} hint="Schedule D line 7" />
          <CurrencyField label="Long-Term Capital Gains" allowNegative value={input.longTermCapitalGains} onChange={v => onChange('longTermCapitalGains', v)} hint="Schedule D line 15" />
          <CurrencyField label="Other Income (Sch 1)" allowNegative value={input.otherIncome} onChange={v => onChange('otherIncome', v)} hint="Schedule 1 → 1040 line 8" />
        </div>
      </div>
    </div>
  );
}
